import {
    Alert,
    Box,
    CircularProgress,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import * as React from 'react';
import { useHistory } from 'react-router-dom';

import { DataAPIContext } from '../../api';
import { listContent } from '../aiChanges/api';
import { OwnerChips } from './common';

/**
 * A read-only list of one content resource (`notification_block`, `block_list`, `notification_theme`,
 * `language_policy`, `language_alias`): text query, Yours / System filter, server paging. A row opens
 * `${basePath}/${id}`.
 *
 * `columns`: `[{ label, render(row) }]` between the codename column and the owner chips.
 */
const ContentList = ({ resource, basePath, columns = [], keyLabel = 'Codename', keyOf = (r) => r.codename, nameOf = (r) => r.name, nameLabel = 'Name', ariaLabel }) => {
    const dataAPI = React.useContext(DataAPIContext);
    const history = useHistory();
    const [query, setQuery] = React.useState('');
    const [debounced, setDebounced] = React.useState('');
    const [system, setSystem] = React.useState('all');
    const [page, setPage] = React.useState(0);
    const [pageSize, setPageSize] = React.useState(25);
    const [state, setState] = React.useState({ loading: true, error: null, rows: [], count: 0 });

    React.useEffect(() => {
        const t = setTimeout(() => setDebounced(query.trim()), 300);
        return () => clearTimeout(t);
    }, [query]);

    React.useEffect(() => {
        let alive = true;
        setState((prev) => ({ ...prev, loading: true, error: null }));
        listContent(dataAPI, resource, {
            page: page + 1,
            pageSize,
            query: debounced || undefined,
            system: system === 'all' ? undefined : system === 'system' ? 'true' : 'false',
        })
            .then((data) => alive && setState({ loading: false, error: null, rows: data?.results || [], count: data?.count ?? 0 }))
            .catch((error) => alive && setState({ loading: false, error, rows: [], count: 0 }));
        return () => {
            alive = false;
        };
    }, [dataAPI, resource, page, pageSize, debounced, system]);

    return (
        <Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
                <TextField
                    size="small"
                    label="Search"
                    value={query}
                    onChange={(ev) => {
                        setQuery(ev.target.value);
                        setPage(0);
                    }}
                />
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={system}
                    onChange={(_ev, v) => {
                        if (v) {
                            setSystem(v);
                            setPage(0);
                        }
                    }}
                >
                    <ToggleButton value="all">All</ToggleButton>
                    <ToggleButton value="own">Yours</ToggleButton>
                    <ToggleButton value="system">System</ToggleButton>
                </ToggleButtonGroup>
                {state.loading && <CircularProgress size={20} />}
            </Stack>
            {state.error && <Alert severity="error">Could not load the list: {state.error.message}</Alert>}
            {!state.error && !state.loading && state.rows.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                    Nothing here.
                </Typography>
            )}
            {state.rows.length > 0 && (
                <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
                    <Table size="small" aria-label={ariaLabel || resource}>
                        <TableHead>
                            <TableRow>
                                <TableCell>{keyLabel}</TableCell>
                                <TableCell>{nameLabel}</TableCell>
                                {columns.map((c) => (
                                    <TableCell key={c.label}>{c.label}</TableCell>
                                ))}
                                <TableCell>Owner</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {state.rows.map((row) => (
                                <TableRow
                                    key={row.id}
                                    hover
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => history.push(`${basePath}/${row.id}`)}
                                >
                                    <TableCell sx={{ fontFamily: 'monospace' }}>{keyOf(row)}</TableCell>
                                    <TableCell>{nameOf(row) || '—'}</TableCell>
                                    {columns.map((c) => (
                                        <TableCell key={c.label}>{c.render(row)}</TableCell>
                                    ))}
                                    <TableCell>
                                        <OwnerChips row={row} />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={state.count}
                        page={page}
                        rowsPerPage={pageSize}
                        rowsPerPageOptions={[25, 50, 100]}
                        onPageChange={(_ev, p) => setPage(p)}
                        onRowsPerPageChange={(ev) => {
                            setPageSize(parseInt(ev.target.value, 10));
                            setPage(0);
                        }}
                    />
                </Paper>
            )}
        </Box>
    );
};

export default ContentList;
